/**
 * Removes persisted user-scoped data on this device (grading cache, plan, communicate, etc.).
 * Used for account deletion and similar “reset my data here” flows — not for a normal sign-out
 * (sign-out intentionally keeps some preferences like appearance).
 */
import { deleteScanGradingDatabase } from './scanGrading/scanIndexedDb';
import { clearAuthSession } from './authSession';
import { clearLastGradeSession } from './lastGradeSession';
import { clearGradingSessionSnapshot } from './trust/gradingSessionSnapshot';
import { clearOfflineActionQueue } from './offlineActionQueue';
import { clearSyncLog } from './syncLog';
import { clearCommunicateArchive } from './communicateArchive';
import { saveFirebaseSession } from '../services/firebaseThreadsRest';
import { setStoredStripeSubscriptionId } from './billingSubscription';
import { clearOfflineGradeSyncIntent } from './offlineGradeSyncIntent';
import { ELITE_FLAGS_KEY, ELITE_METRICS_KEY } from './elitePilot';

const EXACT_KEYS = [
  'dg_educator_name',
  'dg_cache_courses',
  'dg_cache_assignments',
  'dg_cache_students',
  'dg_pending_sync',
  'dg_history',
  'dg_schedule_items_v1',
  'dg_schedule_view_v1',
  'dg_schedule_ui_v1',
  'dg_dash_sort',
  'dg_asn_sort',
  'dg_grade_sync_post_to_classroom_v1',
  'dg_grade_sync_email_students_v1',
  'dg_grade_sync_save_to_drive_v1',
  'dg_grade_followups_v1',
  'dg_quick_todos_v1',
  'dg_nav_usage_v1',
  'dg_attention_expanded_v1',
  'dg_pinned_courses_v1',
  'dg_voice_inbox_expanded_v1',
  'dg_scan_student_mode_v1',
  'dg_batch_roster_sort_v1',
  // Privacy 6C: recovery/offline breadcrumbs the 6A audit found were missed by
  // the original sweep. The IndexedDB scan-page cache is cleared by
  // `clearLocalUserDataAsync` (account deletion) and `clearAllowedLocalBrowserData` (6C).
  'dg_scan_recovery_v1',
  'dg_grade_hub_session_v1',
  'dg_offline_scan_queue_v1',
  'dg_subscription_status',
  ELITE_FLAGS_KEY,
  ELITE_METRICS_KEY,
  'dg_communicate_state_v1',
  'dg_contact_log_sheet_id',
  'dg_comm_voice_draft_v1',
  'dg_threads_notify_v1',
  'dg_threads_quiet_hours_v1',
  'dg_threads_last_seen_v1',
  'dg_communicate_tab_v1',
  'dg_communicate_archive_v1',
  'dg_plan_state_v2',
  'dg_plan_state_v1',
  'dg_file_vault_links',
  'dg_plan_email_recipients',
  'dg_plan_us_state',
  'dg_plan_admin_email',
  'dg_behavior_sheet_id_v1',
  'dg_teach_behavior_log_v1',
  'dg_teach_session_v1',
  'dg_offline_grade_sync_intent_v1',
  'dg_shortcut_usage_v1',
  'dg_analytics_consent',
  'dg_consent_banner_seen',
  'dg_onboarding_done_v1',
  'dg_sidebar_collapsed_v1',
  'dg_options_ui_v1',
] as const;

function removeRubricTemplateKeys(): void {
  if (typeof window === 'undefined') return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith('dg_rubric_templates_')) keys.push(k);
    }
    for (const k of keys) localStorage.removeItem(k);
  } catch {
    /* ignore */
  }
}

export function clearLocalUserData(): void {
  if (typeof window === 'undefined') return;

  clearAuthSession();
  clearLastGradeSession();
  clearGradingSessionSnapshot();
  clearOfflineActionQueue();
  clearSyncLog();
  clearCommunicateArchive();
  clearOfflineGradeSyncIntent();
  saveFirebaseSession(null);
  setStoredStripeSubscriptionId(null);

  for (const k of EXACT_KEYS) {
    try {
      localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  }
  removeRubricTemplateKeys();
}

/** Result of the async local cleanup used during account deletion. */
export interface ClearLocalUserDataResult {
  /** Whether the app-owned local scan cache database was deleted. */
  scanDbCleared: boolean;
}

/**
 * Account-deletion local cleanup: runs {@link clearLocalUserData} then deletes
 * the app-owned scan-pages IndexedDB. Best-effort — a blocked IndexedDB delete
 * does not throw; callers receive `scanDbCleared: false` instead.
 */
export async function clearLocalUserDataAsync(): Promise<ClearLocalUserDataResult> {
  clearLocalUserData();
  const scanDbCleared = await deleteScanGradingDatabase();
  return { scanDbCleared };
}
